import { Award, Code2, Copy, HeartHandshake, Trophy } from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { SEO } from "../components/SEO";
import { FadeUp } from "../components/motion";
import { useLang } from "../i18n/LanguageContext";

const SITE = "https://mrtrophee.ca";

// Page publique « Badge partenaire » : les clubs, ligues et entreprises qui ont
// reçu des récompenses de l'atelier peuvent afficher un badge sur leur site —
// chaque badge installé est un lien vers mrtrophee.ca (backlink naturel).
export default function PartnerBadge() {
  const { t, lang } = useLang();

  const snippetImg = (file, alt) =>
    `<a href="${SITE}" target="_blank" rel="noopener" title="${alt}">\n` +
    `  <img src="${SITE}/${file}" alt="${alt}" width="220" height="64" loading="lazy">\n` +
    `</a>`;
  const snippetText = `<a href="${SITE}" target="_blank" rel="noopener">${t("badge.text_link_preview")}</a>`;

  const copy = async (value) => {
    try {
      await navigator.clipboard.writeText(value);
      toast.success(t("badge.copied"));
    } catch {
      toast.error(lang === "fr" ? "Copie impossible — sélectionnez le texte manuellement" : "Copy failed — select the text manually");
    }
  };

  const badges = [
    { key: "dark", file: "badge-partenaire.svg", previewDark: false },
    { key: "light", file: "badge-partenaire-clair.svg", previewDark: true },
  ];

  return (
    <div className="pt-32 pb-28 max-w-4xl mx-auto px-6 lg:px-10" data-testid="badge-page">
      <SEO
        title={`${t("badge.title")} | Mr Trophée`}
        description={t("badge.text")}
        path="/badge-partenaire"
      />

      <FadeUp>
        <p className="text-gold tracking-[0.3em] uppercase text-xs mb-4 flex items-center gap-2">
          <HeartHandshake className="w-4 h-4" /> {t("badge.page_kicker")}
        </p>
        <h1 className="font-serif text-4xl md:text-5xl leading-tight mb-4" data-testid="badge-page-title">{t("badge.title")}</h1>
        <p className="text-ivory/60 text-sm leading-relaxed max-w-2xl mb-12">{t("badge.text")}</p>
      </FadeUp>

      {/* Comment ça fonctionne */}
      <FadeUp delay={0.08}>
        <h2 className="text-xs uppercase tracking-widest text-ivory/65 mb-4">{t("badge.how_title")}</h2>
        <div className="grid sm:grid-cols-3 gap-4 mb-12">
          {["how_1", "how_2", "how_3"].map((k, i) => (
            <div key={k} className="bg-surface border border-ivory/10 rounded-xl p-5" data-testid={`badge-step-${i}`}>
              <p className="font-serif text-3xl text-gold mb-2">{i + 1}</p>
              <p className="text-sm text-ivory/70 leading-relaxed">{t(`badge.${k}`)}</p>
            </div>
          ))}
        </div>
      </FadeUp>

      {/* Les badges à copier */}
      <div className="space-y-6">
        {badges.map((b, i) => (
          <FadeUp key={b.key} delay={0.1 + i * 0.04}>
            <div className="bg-surface border border-gold/20 rounded-xl p-7" data-testid={`badge-card-${b.key}`}>
              <h2 className="text-xs uppercase tracking-widest text-ivory/65 mb-5 flex items-center gap-2">
                <Trophy className="w-4 h-4 text-gold" /> {t(`badge.${b.key}_title`)}
              </h2>
              <div className={`rounded-lg p-8 mb-5 flex items-center justify-center ${b.previewDark ? "bg-[#0a0a0a]" : "bg-[#f4f1ea]"}`}
                data-testid={`badge-preview-${b.key}`}>
                <img src={`/${b.file}`} alt={t("badge.alt")} width="220" height="64" />
              </div>
              <div className="flex flex-wrap items-start gap-3">
                <pre data-testid={`badge-code-${b.key}`}
                  className="flex-1 min-w-64 bg-ink border border-ivory/15 rounded-lg px-4 py-3 text-xs text-champagne overflow-x-auto whitespace-pre-wrap break-all">
                  {snippetImg(b.file, t("badge.alt"))}
                </pre>
                <button data-testid={`badge-copy-${b.key}`} onClick={() => copy(snippetImg(b.file, t("badge.alt")))}
                  className="inline-flex items-center gap-2 bg-gold text-ink font-semibold px-5 py-3 rounded-full text-sm hover:bg-champagne transition-colors duration-300">
                  <Copy className="w-4 h-4" /> {t("badge.copy")}
                </button>
              </div>
            </div>
          </FadeUp>
        ))}

        {/* Lien texte */}
        <FadeUp delay={0.18}>
          <div className="bg-surface border border-ivory/10 rounded-xl p-7" data-testid="badge-card-text">
            <h2 className="text-xs uppercase tracking-widest text-ivory/65 mb-5 flex items-center gap-2">
              <Code2 className="w-4 h-4 text-gold" /> {t("badge.text_title")}
            </h2>
            <p className="mb-5">
              <a href={SITE} target="_blank" rel="noopener" data-testid="badge-text-preview"
                className="text-gold underline underline-offset-4 hover:text-champagne transition-colors">
                {t("badge.text_link_preview")}
              </a>
            </p>
            <div className="flex flex-wrap items-start gap-3">
              <pre data-testid="badge-code-text"
                className="flex-1 min-w-64 bg-ink border border-ivory/15 rounded-lg px-4 py-3 text-xs text-champagne overflow-x-auto whitespace-pre-wrap break-all">
                {snippetText}
              </pre>
              <button data-testid="badge-copy-text" onClick={() => copy(snippetText)}
                className="inline-flex items-center gap-2 border border-gold/40 text-gold px-5 py-3 rounded-full text-sm hover:bg-gold hover:text-ink transition-colors duration-300">
                <Copy className="w-4 h-4" /> {t("badge.copy")}
              </button>
            </div>
          </div>
        </FadeUp>
      </div>

      {/* Pourquoi */}
      <FadeUp delay={0.22}>
        <div className="bg-surface border border-ivory/10 rounded-xl p-7 mt-10" data-testid="badge-why">
          <h2 className="text-xs uppercase tracking-widest text-ivory/65 mb-3 flex items-center gap-2">
            <Award className="w-4 h-4 text-gold" /> {t("badge.why_title")}
          </h2>
          <p className="text-sm text-ivory/70 leading-relaxed mb-5">{t("badge.why_text")}</p>
          <Link to="/contact" data-testid="badge-contact-cta"
            className="inline-flex items-center gap-2 border border-gold/40 text-gold px-6 py-2.5 rounded-full text-sm hover:bg-gold hover:text-ink transition-colors duration-300">
            {t("badge.cta")}
          </Link>
        </div>
      </FadeUp>

      <p className="text-[11px] text-ivory/60 mt-10 text-center" data-testid="badge-note">{t("badge.note")}</p>
    </div>
  );
}
